import {
  Directive,
  ElementRef,
  HostListener,
  Optional,
  Self,
} from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appMacAddressFormat]',
})
export class MacAddressFormatDirective {
  private isComposing = false;

  constructor(
    private readonly el: ElementRef,
    @Optional() @Self() private readonly control: NgControl,
  ) {}

  @HostListener('compositionstart')
  onCompositionStart() {
    this.isComposing = true;
  }

  @HostListener('compositionend', ['$event'])
  onCompositionEnd(e: CompositionEvent) {
    this.isComposing = false;
    this.normaliseAndSet(e.target as HTMLInputElement);
  }

  @HostListener('paste', ['$event'])
  onPaste(e: ClipboardEvent) {
    e.preventDefault();
    const input = e.target as HTMLInputElement;
      let text = (e.clipboardData || (window as any).clipboardData)?.getData('text') ?? '';
      text = text.toUpperCase();

      // Insert the pasted text at the current selection
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? start;
      const next = input.value.slice(0, start) + text + input.value.slice(end);

      input.value = next;
      // Now normalise the whole value and set caret correctly
      this.normaliseAndSet(input, true);
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    if (this.isComposing) return;
    const input = event.target as HTMLInputElement;
    this.normaliseAndSet(input);
  }

  private normaliseAndSet(input: HTMLInputElement, preferEnd = false) {
    const prevValue = input.value;
    const prevCursor = input.selectionStart ?? prevValue.length;

    // Count HEX chars before caret
    const hexBeforeCaret = prevValue
      .slice(0, prevCursor)
      .replace(/[^0-9a-fA-F]/g, '').length;

    // 1) Allow HEX + ":" and uppercase
    let value = prevValue.replace(/[^0-9a-fA-F:]/g, '').toUpperCase();

    // 2) Collapse duplicate colons
    value = value.replace(/:{2,}/g, ':');

    // 3) Cap to 12 HEX chars
    const hexOnly = value.replace(/:/g, '').slice(0, 12);

    // 4) Format with ":" every two HEX chars
    const formatted = hexOnly.replace(/(.{2})(?=.)/g, '$1:');

    // Update DOM (and caret) only if the visible string changed
    if (formatted !== prevValue) {
      const targetHexCount = preferEnd
        ? hexOnly.length
        : Math.min(hexBeforeCaret, hexOnly.length);
      let nextCursor = 0,
        seenHex = 0;
      while (nextCursor < formatted.length && seenHex < targetHexCount) {
        if (/[0-9A-F]/.test(formatted[nextCursor])) seenHex++;
        nextCursor++;
      }
      input.value = formatted;
      input.setSelectionRange(nextCursor, nextCursor);
    }

    // Sync the FormControl so validators (e.g., required) re-run
    const ctrl = this.control?.control;

    if (ctrl && ctrl.value !== formatted) {
      ctrl.setValue(formatted, { emitEvent: true });
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity({ emitEvent: true });
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.isComposing) return;

    // Allow common shortcuts (Ctrl/Cmd + …)
    if (event.ctrlKey || event.metaKey) {
      const k = event.key.toLowerCase();
      if (['v', 'c', 'x', 'a', 'z', 'y'].includes(k)) {
        // paste, copy, cut, select-all, undo, redo
        return;
      }
    }

    // Allow Shift+Insert (paste on Windows)
    if (event.shiftKey && event.key === 'Insert') return;

    // Allow navigation/editing keys
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'PageUp',
      'PageDown',
    ];
    if (allowedKeys.includes(event.key)) return;

    // Allow hex digits and ":" (so user can type colons)
    if (/^[0-9a-fA-F:]$/.test(event.key)) return;

    // Block everything else
    event.preventDefault();
  }
}
